"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { captureFrame, mediaError, stopStream } from "@/lib/navigation/vision/media";
import { EncounterTracker } from "@/lib/navigation/vision/tracker";
import type { DetectionResult, ObjectTrack, VisualEncounter } from "@/lib/navigation/vision/types";
import type { useVisionDetector } from "./useVisionDetector";

export interface MediaSource {
    kind: "idle" | "camera" | "video" | "photo";
    label: string; url: string; width: number; height: number;
}
export interface FrameStats { frames: number; timestampMs: number; inferenceMs: number; fps: number; detections: number }
const EMPTY_STATS: FrameStats = { frames: 0, timestampMs: 0, inferenceMs: 0, fps: 0, detections: 0 };

export function useVisionSession(detector: ReturnType<typeof useVisionDetector>, options: {
    threshold: number; fps: number; onReset: () => void; onEncounters: (encounters: VisualEncounter[]) => void;
}) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const frameCanvas = useRef<HTMLCanvasElement | null>(null);
    const stream = useRef<MediaStream | null>(null);
    const sourceRef = useRef<MediaSource>({ kind: "idle", label: "", url: "", width: 1280, height: 720 });
    const [source, setSource] = useState(sourceRef.current);
    const [sourceVersion, setSourceVersion] = useState(0);
    const [running, setRunning] = useState(false);
    const [paused, setPaused] = useState(false);
    const [starting, setStarting] = useState(false);
    const [tracks, setTracks] = useState<ObjectTrack[]>([]);
    const [stats, setStats] = useState<FrameStats>(EMPTY_STATS);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [devices, setDevices] = useState<{ id: string; label: string }[]>([]);
    const [videoReady, setVideoReady] = useState(false);
    const mounted = useRef(true);
    const control = useRef<AbortController | null>(null);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const active = useRef(false);
    const latest = useRef({ detector, options });
    useEffect(() => { latest.current = { detector, options }; });

    const stop = useCallback((reason = "Аналіз зупинено. Журнал доступний для перегляду й експорту.") => {
        control.current?.abort(); control.current = null; active.current = false;
        if (timer.current) clearTimeout(timer.current);
        timer.current = null;
        videoRef.current?.pause(); stopStream(stream.current); stream.current = null;
        if (videoRef.current?.srcObject) videoRef.current.srcObject = null;
        if (mounted.current) { setRunning(false); setPaused(false); setStarting(false); setMessage(reason); if (sourceRef.current.kind === "camera") setTracks([]); }
    }, []);

    useEffect(() => {
        mounted.current = true;
        const visibility = () => { if (document.hidden) stop("Вкладку приховано — аналіз і камеру зупинено."); };
        const leave = () => stop();
        document.addEventListener("visibilitychange", visibility);
        window.addEventListener("pagehide", leave);
        return () => {
            mounted.current = false; stop();
            if (sourceRef.current.url.startsWith("blob:")) URL.revokeObjectURL(sourceRef.current.url);
            document.removeEventListener("visibilitychange", visibility); window.removeEventListener("pagehide", leave);
        };
    }, [stop]);

    function updateSource(next: MediaSource) { sourceRef.current = next; setSource(next); }
    function resetSource(next: MediaSource) {
        stop("");
        if (sourceRef.current.url.startsWith("blob:")) URL.revokeObjectURL(sourceRef.current.url);
        if (videoRef.current) { videoRef.current.removeAttribute("src"); videoRef.current.load(); }
        updateSource(next); setSourceVersion(version => version + 1); setVideoReady(false); setTracks([]); setStats(EMPTY_STATS); setError(""); latest.current.options.onReset();
    }
    function begin() {
        stop(""); setError(""); setStarting(true); setStats(EMPTY_STATS); setTracks([]);
        latest.current.options.onReset();
        const controller = new AbortController(); control.current = controller;
        const tracker = new EncounterTracker(crypto.randomUUID(), { threshold: latest.current.options.threshold });
        frameCanvas.current ??= document.createElement("canvas");
        return { controller, tracker };
    }

    function processVideo(controller: AbortController, tracker: EncounterTracker) {
        const settings = { threshold: latest.current.options.threshold, fps: latest.current.options.fps };
        let lastFrame = -1, frames = 0, startedAt = performance.now();
        const tick = async () => {
            if (controller.signal.aborted || !active.current) return;
            const video = videoRef.current;
            if (!video) return;
            if (video.ended) { stop("Відео завершено. Можна експортувати журнал або почати новий аналіз."); return; }
            if (video.paused || video.seeking || video.readyState < 2 || video.currentTime * 1000 <= lastFrame + 80) {
                timer.current = setTimeout(tick, 120); return;
            }
            const frameStarted = performance.now();
            const timestampMs = Math.max(0, Math.round(video.currentTime * 1000));
            lastFrame = timestampMs;
            try {
                const frame = captureFrame(video, frameCanvas.current!);
                const detection = await latest.current.detector.detect(frame, settings.threshold, controller.signal);
                if (controller.signal.aborted || !mounted.current) return;
                const result = tracker.update(detection.detections, timestampMs);
                frames++; if (frames === 1) startedAt = frameStarted;
                setTracks(result.tracks);
                setStats({ frames, timestampMs, inferenceMs: detection.inferenceMs, fps: frames > 1 ? (frames - 1) / Math.max(0.1, (frameStarted - startedAt) / 1000) : 0, detections: detection.detections.length });
                if (result.encounters.length) latest.current.options.onEncounters(result.encounters);
            } catch (failure) {
                if (controller.signal.aborted || !mounted.current) return;
                stop(); setError(mediaError(failure)); return;
            }
            if (!controller.signal.aborted) timer.current = setTimeout(tick, Math.max(0, 1000 / settings.fps - (performance.now() - frameStarted)));
        };
        active.current = true; setRunning(true); setStarting(false); setMessage("");
        void tick();
    }

    async function startCamera(deviceId: string) {
        const { controller, tracker } = begin();
        if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
            stop(); setError("Камера потребує HTTPS і браузера з підтримкою getUserMedia. Можна відкрити відеофайл або фото."); return;
        }
        // Request permission directly in the click handler, before loading ML.
        try {
            const media = navigator.mediaDevices.getUserMedia({ audio: false, video: {
                ...(deviceId ? { deviceId: { exact: deviceId } } : { facingMode: { ideal: "environment" } }),
                width: { ideal: 1280 }, height: { ideal: 720 },
            } });
            const camera = await media;
            if (controller.signal.aborted || !mounted.current) { stopStream(camera); return; }
            stream.current = camera;
            const track = camera.getVideoTracks()[0];
            const settings = track.getSettings();
            if (sourceRef.current.url.startsWith("blob:")) URL.revokeObjectURL(sourceRef.current.url);
            updateSource({ kind: "camera", label: track.label || "Камера", url: "", width: settings.width ?? 1280, height: settings.height ?? 720 });
            const video = videoRef.current!;
            video.removeAttribute("src"); video.srcObject = camera;
            track.addEventListener("ended", () => { if (stream.current === camera) stop("Камеру від’єднано або доступ до неї припинено."); });
            await video.play();
            await latest.current.detector.initialize();
            if (controller.signal.aborted || !mounted.current) return;
            const available = typeof navigator.mediaDevices.enumerateDevices === "function"
                ? await navigator.mediaDevices.enumerateDevices().catch(() => []) : [];
            if (controller.signal.aborted || !mounted.current) return;
            setDevices(available.filter(device => device.kind === "videoinput").map((device, index) => ({ id: device.deviceId, label: device.label || `Камера ${index + 1}` })));
            processVideo(controller, tracker);
        } catch (failure) {
            if (controller.signal.aborted || !mounted.current) return;
            stop(); setError(mediaError(failure));
        }
    }

    function openFile(file: File) {
        const photo = ["image/jpeg", "image/png", "image/webp"].includes(file.type);
        if (!photo && !file.type.startsWith("video/")) { setError("Оберіть відео або зображення JPEG, PNG чи WebP."); return; }
        const url = URL.createObjectURL(file);
        resetSource({ kind: photo ? "photo" : "video", label: file.name, url, width: 1280, height: 720 });
        if (!photo && videoRef.current) { videoRef.current.src = url; videoRef.current.load(); setMessage("Відео відкрите. Оберіть початок і натисніть «Аналізувати відео»."); }
    }

    async function startVideo() {
        const video = videoRef.current;
        if (!video || sourceRef.current.kind !== "video" || !video.videoWidth) { setError("Зачекайте завантаження відео."); return; }
        const { controller, tracker } = begin();
        try {
            await latest.current.detector.initialize();
            if (controller.signal.aborted || !mounted.current) return;
            if (video.ended) video.currentTime = 0;
            await video.play();
            if (!controller.signal.aborted && mounted.current) processVideo(controller, tracker);
        } catch (failure) {
            if (!controller.signal.aborted && mounted.current) { stop(); setError(mediaError(failure)); }
        }
    }

    function openSample(url: string, label: string) { resetSource({ kind: "photo", label, url, width: 1280, height: 960 }); }

    async function analyzePhoto() {
        const image = imageRef.current;
        if (!image || !image.complete || !image.naturalWidth || !image.naturalHeight || sourceRef.current.kind !== "photo" || image.src !== new URL(sourceRef.current.url, window.location.href).href) return;
        const { controller } = begin();
        updateSource({ ...sourceRef.current, width: image.naturalWidth, height: image.naturalHeight });
        try {
            const detection: DetectionResult = await latest.current.detector.detect(captureFrame(image, frameCanvas.current!), latest.current.options.threshold, controller.signal);
            if (controller.signal.aborted || !mounted.current) return;
            // A still image tests inference; it is not several independent frames.
            setTracks(detection.detections.map((item, index) => ({ ...item, id: `photo-${index}`, hits: 1, firstSeenMs: 0, lastSeenMs: 0, confirmed: false })));
            setStats({ frames: 1, timestampMs: 0, inferenceMs: detection.inferenceMs, fps: 0, detections: detection.detections.length });
            setMessage("Фото проаналізовано. Для локалізації потрібна послідовність відеоспостережень.");
        } catch (failure) { if (!controller.signal.aborted && mounted.current) setError(mediaError(failure)); }
        finally { if (!controller.signal.aborted && mounted.current) setStarting(false); }
    }

    function metadataLoaded() {
        const video = videoRef.current;
        if (video?.videoWidth && sourceRef.current.kind !== "photo") {
            const width = video.videoWidth, height = video.videoHeight;
            if (active.current && (sourceRef.current.width !== width || sourceRef.current.height !== height)) {
                stop("Формат відеопотоку змінився. Запустіть нову сесію аналізу.");
            }
            setVideoReady(true); updateSource({ ...sourceRef.current, width, height });
        }
    }
    function seeked() {
        if (!active.current || sourceRef.current.kind !== "video") return;
        stop("Позицію відео змінено. Запустіть аналіз з нового місця."); setTracks([]); setStats(EMPTY_STATS); latest.current.options.onReset();
    }
    function decodingError(element: "photo" | "video") {
        if (element === "photo" && sourceRef.current.kind !== "photo") return;
        if (element === "video" && sourceRef.current.kind !== "video" && sourceRef.current.kind !== "camera") return;
        stop(); setError("Браузер не зміг відкрити цей медіафайл. Спробуйте MP4/H.264, WebM або JPEG.");
    }
    function playbackChanged() { setPaused(active.current && sourceRef.current.kind === "video" && !!videoRef.current?.paused); }

    return { videoRef, imageRef, source, sourceVersion, running, paused, starting, tracks, stats, message, error, devices, videoReady,
        startCamera, stop, openFile, openSample, startVideo, analyzePhoto, metadataLoaded, seeked, decodingError, playbackChanged };
}
